import type { EnumLike } from '@pkg/shared/types'
import { z } from 'zod'

export const Q = {
  // filter<Types extends readonly [core.$ZodTypeDiscriminable, ...core.$ZodTypeDiscriminable[]]>(types: Types) {
  //   return z.discriminatedUnion('operator', types)
  // },
  eq<T extends z.ZodTypeAny>(value: T) {
    return z.object({ operator: z.literal('eq'), value })
  },
  ne<T extends z.ZodTypeAny>(value: T) {
    return z.object({ operator: z.literal('ne'), value })
  },
  gt<T extends z.ZodTypeAny>(value: T) {
    return z.object({ operator: z.literal('gt'), value })
  },
  gte<T extends z.ZodTypeAny>(value: T) {
    return z.object({ operator: z.literal('gte'), value })
  },
  lt<T extends z.ZodTypeAny>(value: T) {
    return z.object({ operator: z.literal('lt'), value })
  },
  lte<T extends z.ZodTypeAny>(value: T) {
    return z.object({ operator: z.literal('lte'), value })
  },
  like() {
    return z.object({ operator: z.literal('like'), value: z.string() })
  },
  in<T extends z.ZodTypeAny>(value: T) {
    return z.object({ operator: z.literal('in'), value: z.array(value) })
  },
  between<T extends z.ZodTypeAny>(value: T) {
    return z.object({
      operator: z.literal('between'),
      value: z.object({ from: value, to: value }),
    })
  },
  all<T extends z.ZodTypeAny>(value: T) {
    return z.discriminatedUnion('operator', [
      Q.eq(value),
      Q.ne(value),
      Q.gt(value),
      Q.gte(value),
      Q.lt(value),
      Q.lte(value),
      Q.like(),
      Q.in(value),
      Q.between(value),
    ])
  },
  id<T extends z.ZodTypeAny>(value: T) {
    return z.discriminatedUnion('operator', [Q.eq(value), Q.in(value)])
  },
  enum<T extends EnumLike>(values: T) {
    const asEnumValue = z.enum(values)
    return z.discriminatedUnion('operator', [Q.eq(asEnumValue), Q.ne(asEnumValue), Q.in(asEnumValue)])
  },
}

export type QHelpers = typeof Q

export type QueryFilterOperator = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'in' | 'between'

export const F = {
  eq<T>(value: T) {
    return { operator: 'eq' as const, value }
  },
  ne<T>(value: T) {
    return { operator: 'ne' as const, value }
  },
  gt<T>(value: T) {
    return { operator: 'gt' as const, value }
  },
  gte<T>(value: T) {
    return { operator: 'gte' as const, value }
  },
  lt<T>(value: T) {
    return { operator: 'lt' as const, value }
  },
  lte<T>(value: T) {
    return { operator: 'lte' as const, value }
  },
  like(value: string) {
    return { operator: 'like' as const, value }
  },
  in<T>(value: ReadonlyArray<T>) {
    return { operator: 'in' as const, value }
  },
  between<T>(value: { from: T; to: T }) {
    return { operator: 'between' as const, value }
  },
}

// Type unions for operator entries (value types derived from Zod schemas)
export type OpEq<T extends z.ZodTypeAny> = { operator: 'eq'; value: z.infer<T> }
export type OpNe<T extends z.ZodTypeAny> = { operator: 'ne'; value: z.infer<T> }
export type OpGt<T extends z.ZodTypeAny> = { operator: 'gt'; value: z.infer<T> }
export type OpGte<T extends z.ZodTypeAny> = { operator: 'gte'; value: z.infer<T> }
export type OpLt<T extends z.ZodTypeAny> = { operator: 'lt'; value: z.infer<T> }
export type OpLte<T extends z.ZodTypeAny> = { operator: 'lte'; value: z.infer<T> }
export type OpLike = { operator: 'like'; value: string }
export type OpIn<T extends z.ZodTypeAny> = { operator: 'in'; value: ReadonlyArray<z.infer<T>> }
export type OpBetween<T extends z.ZodTypeAny> = {
  operator: 'between'
  value: { from: z.infer<T>; to: z.infer<T> }
}

export type OperationAll<T extends z.ZodTypeAny> =
  | OpEq<T>
  | OpNe<T>
  | OpGt<T>
  | OpGte<T>
  | OpLt<T>
  | OpLte<T>
  | OpLike
  | OpIn<T>
  | OpBetween<T>

export type OperationBool = OpEq<z.ZodBoolean> | OpNe<z.ZodBoolean>

export type OperationId<T extends z.ZodTypeAny> = OpEq<T> | OpIn<T>
